import type { Node, NodeProps } from '@xyflow/react';
import { ZfCard, ZfCardContent, ZfDropdown, ZfDropdownItem, ZfIcon, ZfLoadingSpinner, ZfPill } from '@zafire/react';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient } from '../../../app/api';
import { useAppEnvironment } from '../../../app/contexts/app-environment-context';
import { useShadowRootCustomStyles } from '../../../app/hooks/useShadowRootCustomStyles';
import { useWealthPlanningNavigate } from '../../../app/hooks/useWealthPlanningNavigate';
import { useFamilyTree } from '../../api';
import { partnerNodeSize } from '../../config/constants';
import { useElementHovering } from '../../hooks';
import type { PartnerNodeData } from '../../interfaces';
import type { PartnerDTO } from '../../types/PartnerRelation';
import { PreselectedRelationshipType } from '../../types/PartnerRelation';
import { hasHandleOnSide } from '../../utils';
import { CreatePartnerRelationModal } from '../partner-data/CreatePartnerRelationModal';
import { DeletePartnerRelationModal } from '../partner-data/DeletePartnerRelationModal';
import { UpdatePartnerRelationModal } from '../partner-data/UpdatePartnerRelationModal';
import { NodeHandlers } from './node-handlers';
import { NodeVisibilityButton } from './node-visibility-button';
import { PartnerNodeAddIcons } from './partner-node-add-icons';
import { PartnerNodeContent } from './partner-node-content';

type PartnerNodeType = Node<PartnerNodeData, 'familyTreeNode'>;

export const PartnerNode: FunctionComponent<NodeProps<PartnerNodeType>> = ({ id, data }) => {
    const showAddIcons = data.showAddIcons ?? true;
    const showHideIcon = data.showHideIcon ?? true;

    const { t } = useTranslation();
    const { clmBaseUrl } = useAppEnvironment();
    const cardContentRef = useRef<HTMLZfCardContentElement>(null);
    const { partnerUuid } = useWealthPlanningNavigate();
    const { data: familyTree, isLoading: isLoadingFamilyTree } = useFamilyTree(partnerUuid);
    const { data: client, isLoading: isLoadingClient } = useClient(partnerUuid);

    const availablePartners: PartnerDTO[] =
        familyTree?.partnerNodes?.map((node) => ({
            globalPartnerId: node.globalPartnerId,
            fullName: `${node.firstName} ${node.lastName}`.trim(),
            techId: node.uuid,
        })) ?? [];

    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

    const [preselectedRelationshipType, setPreselectedRelationshipType] = useState<PreselectedRelationshipType>(PreselectedRelationshipType.IS_PARENT);

    useShadowRootCustomStyles(
        cardContentRef,
        '.card-content { padding: var(--padding-vertical-xxs) !important; gap: 0 !important; justify-content: normal !important; }',
    );

    const isHovered = useElementHovering(cardContentRef, true);

    const isDeceased = useMemo(() => !!data.dateOfDeath && moment(data.dateOfDeath).isBefore(moment()), [data.dateOfDeath]);
    const partnerTypeColor = useMemo(() => {
        if (data.partnerType === 'CLIENT') return 'blue';

        return data.partnerType === 'PROSPECT' ? 'yellow' : 'neutral';
    }, [data.partnerType]);

    const onOpenInClmClick = () => window.open(`${clmBaseUrl}/partner/private-individual/edit/${id}/core-data`, '_blank', 'noopener,noreferrer');

    const onAddIconClick = (relationshipType: PreselectedRelationshipType) => {
        setPreselectedRelationshipType(relationshipType);
        setIsCreateModalOpen(true);
    };

    if (isLoadingFamilyTree || isLoadingClient) return <ZfLoadingSpinner />;

    return (
        <ZfCard orientation="vertical" clickable={false}>
            <ZfCardContent
                className={`partner-node ${isDeceased ? 'partner-node--disabled' : ''} ${data.isPrintMode ? 'print-mode' : ''}`}
                ref={cardContentRef}
                style={{ width: `${partnerNodeSize.width}px`, height: `${partnerNodeSize.height}px` }}
            >
                {isHovered && showAddIcons && (
                    <PartnerNodeAddIcons
                        usedSides={{
                            top: hasHandleOnSide('top', data.usedHandles),
                            left: hasHandleOnSide('left', data.usedHandles),
                            right: hasHandleOnSide('right', data.usedHandles),
                            bottom: hasHandleOnSide('bottom', data.usedHandles),
                        }}
                        onAddIconClick={onAddIconClick}
                    />
                )}
                <div className="partner-node-header">
                    {!data.isPresentationMode && !data.isMainPartner && showHideIcon && (
                        <NodeVisibilityButton partnerUuid={partnerUuid} nodeUuid={data.uuid} visibility={data.visibility} />
                    )}
                    {!data.isPrintMode && (
                        <ZfDropdown side="left" icon="more_vert" hierarchy="tertiary" size="extra-small">
                            <ZfDropdownItem label={t('removeRelationship')} onClick={() => setIsDeleteModalOpen(true)}>
                                <ZfIcon slot="trailing" name="delete" size="large" />
                            </ZfDropdownItem>
                            <ZfDropdownItem label={t('updateRelationship')} onClick={() => setIsUpdateModalOpen(true)}>
                                <ZfIcon slot="trailing" name="edit" size="large" />
                            </ZfDropdownItem>
                            <ZfDropdownItem label={t('openInClm')} onClick={onOpenInClmClick}>
                                <ZfIcon slot="trailing" name="open_in_new" size="large" />
                            </ZfDropdownItem>
                        </ZfDropdown>
                    )}
                    {isCreateModalOpen && (
                        <CreatePartnerRelationModal
                            partnerUuid={partnerUuid}
                            sourceRelations={[
                                { partnerUuid: data.uuid, globalPartnerId: data.globalPartnerId, fullName: `${data.firstName} ${data.lastName}` },
                            ]}
                            availablePartners={availablePartners}
                            relationshipManagerUserId={client?.relationshipManager?.userId}
                            onModalClose={() => setIsCreateModalOpen(false)}
                            preselectedRelationshipType={preselectedRelationshipType}
                        />
                    )}
                    {isDeleteModalOpen && (
                        <DeletePartnerRelationModal mainPartnerUuid={partnerUuid} partnerUuid={data.uuid} onModalClose={() => setIsDeleteModalOpen(false)} />
                    )}
                    {isUpdateModalOpen && (
                        <UpdatePartnerRelationModal
                            mainPartnerUuid={partnerUuid}
                            partnerUuid={data.uuid}
                            relationshipManagerUserId={client?.relationshipManager?.userId}
                            onModalClose={() => setIsUpdateModalOpen(false)}
                        />
                    )}
                </div>
                <PartnerNodeContent data={data} isDeceased={isDeceased} isHovered={isHovered} />
                {!data.isPresentationMode ? (
                    <div className="partner-node-footer">
                        <ZfPill clickable={false} color={partnerTypeColor} size="small">
                            {t(`familyTree-${data.partnerType}`)}
                        </ZfPill>
                    </div>
                ) : null}
            </ZfCardContent>
            <NodeHandlers usedHandles={data.usedHandles} isConnectorNode={false} />
        </ZfCard>
    );
};
